import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronRight } from 'lucide-react';
import Header from './components/Header11';
import Footer from './components/Footer11';
import ProductCard from './components/ProductCard';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 10000]);

    const categories = ['All', 'Women', 'Men', 'Accessories', 'Footwear'];
    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest' },
    ];

    const products = [
        {
            id: 1,
            name: 'Silk Evening Gown',
            category: 'Women',
            price: 8999,
            originalPrice: 12999,
            image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600',
        },
        {
            id: 2,
            name: 'Cashmere Blazer',
            category: 'Men',
            price: 15999,
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600',
        },
        {
            id: 3,
            name: 'Leather Clutch',
            category: 'Accessories',
            price: 4999,
            originalPrice: 6999,
            image: 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=600',
        },
        {
            id: 4,
            name: 'Italian Loafers',
            category: 'Footwear',
            price: 12999,
            image: 'https://images.unsplash.com/photo-1533867617858-e7b97e060509?w=600',
        },
        {
            id: 5,
            name: 'Tailored Suit',
            category: 'Men',
            price: 24999,
            image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=600',
        },
        {
            id: 6,
            name: 'Pearl Necklace',
            category: 'Accessories',
            price: 18999,
            image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600',
        },
        {
            id: 7,
            name: 'Velvet Dress',
            category: 'Women',
            price: 9999,
            image: 'https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=600',
        },
        {
            id: 8,
            name: 'Oxford Shoes',
            category: 'Footwear',
            price: 11999,
            originalPrice: 14999,
            image: 'https://images.unsplash.com/photo-1614252368041-7cb49aae3820?w=600',
        },
        {
            id: 9,
            name: 'Designer Handbag',
            category: 'Accessories',
            price: 22999,
            image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600',
        },
        {
            id: 10,
            name: 'Wool Coat',
            category: 'Women',
            price: 19999,
            image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=600',
        },
        {
            id: 11,
            name: 'Dress Shirt',
            category: 'Men',
            price: 6999,
            image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600',
        },
        {
            id: 12,
            name: 'Chelsea Boots',
            category: 'Footwear',
            price: 13999,
            image: 'https://images.unsplash.com/photo-1608256246200-53e635b5b65f?w=600',
        },
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-gray-900 text-white font-serif">
            <Header cartCount={3} />

            <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
                {/* Page Header */}
                <div className="mb-12 lg:mb-16 text-center">
                    <p className="text-sm tracking-widest mb-4 text-white/70">CURATED COLLECTION</p>
                    <h1 className="text-5xl lg:text-6xl font-light mb-6">Luxury Shop</h1>
                    <p className="text-lg text-white/80 max-w-2xl mx-auto">
                        Discover timeless pieces crafted with exceptional attention to detail
                    </p>
                </div>

                {/* Search and Controls */}
                <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-4xl mx-auto">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                        <input
                            type="text"
                            placeholder="Search luxury items..."
                            className="w-full pl-12 pr-4 py-3 bg-transparent border border-gray-700 text-white placeholder-white/40 focus:outline-none focus:border-white/50 transition-colors"
                        />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-4 py-3 bg-transparent border border-gray-700 text-white focus:outline-none focus:border-white/50 transition-colors"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value} className="bg-gray-900">{option.label}</option>
                        ))}
                    </select>

                    {/* Filter Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="px-6 py-3 border border-gray-700 hover:border-white/50 transition-colors flex items-center justify-center gap-2"
                    >
                        <SlidersHorizontal size={20} />
                        <span className="text-sm tracking-wider">FILTERS</span>
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-gray-800 border border-gray-700 p-6 mb-8 max-w-4xl mx-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-light text-lg tracking-wider">REFINE SELECTION</h3>
                            <button onClick={() => setShowFilters(false)} className="text-white/70 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Price Range */}
                            <div>
                                <h4 className="font-light mb-3 tracking-wide text-sm text-white/70">PRICE RANGE</h4>
                                <div className="space-y-2">
                                    <input
                                        type="range"
                                        min="0"
                                        max="30000"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-sm text-white/60">
                                        <span>₹0</span>
                                        <span>₹{priceRange[1]}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Size */}
                            <div>
                                <h4 className="font-light mb-3 tracking-wide text-sm text-white/70">SIZE</h4>
                                <div className="flex flex-wrap gap-2">
                                    {['XS', 'S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            className="px-4 py-2 border border-gray-700 hover:border-white hover:bg-white hover:text-gray-900 transition-colors text-sm tracking-wider"
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Material */}
                            <div>
                                <h4 className="font-light mb-3 tracking-wide text-sm text-white/70">MATERIAL</h4>
                                <div className="space-y-2">
                                    {['Silk', 'Cashmere', 'Leather', 'Wool'].map(material => (
                                        <label key={material} className="flex items-center gap-2 cursor-pointer text-sm text-white/80 hover:text-white transition-colors">
                                            <input type="checkbox" className="form-checkbox bg-transparent border-gray-700" />
                                            <span className="tracking-wide">{material}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Tabs */}
                <div className="flex flex-wrap justify-center gap-8 mb-12">
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category.toLowerCase())}
                            className={`text-sm tracking-widest transition-colors pb-2 ${
                                selectedCategory === category.toLowerCase()
                                    ? 'border-b border-white text-white'
                                    : 'text-white/60 hover:text-white/80 border-b border-transparent'
                            }`}
                        >
                            {category.toUpperCase()}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>

                {/* Load More */}
                <div className="text-center mt-16">
                    <button className="group inline-flex items-center gap-3 text-white border-b border-white/30 hover:border-white pb-2 transition-all">
                        <span className="text-sm tracking-widest">VIEW MORE</span>
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Shop;
